import { useNavigate } from "flemo";
import { ChevronRightIcon } from "lucide-react";
import { motion } from "motion/react";

import { Skeleton } from "@/components/ui/skeleton";
import { useLearnedCountsQuery, useWordSetsQuery } from "@/hooks/queries";

type ProgressItem = {
  id: string;
  title: string;
  knownCount: number;
  learningCount: number;
  totalCount: number;
};

export function SetProgress() {
  const { push } = useNavigate();
  const { data: wordSets = [], isPending: isWordSetsPending } = useWordSetsQuery();
  const { data: learnedCounts = {}, isPending: isCountsPending } = useLearnedCountsQuery();

  const isPending = isWordSetsPending || isCountsPending;

  // 진행 중인 세트만 필터 (1개 이상 학습 && 미완료), 진행률 높은 순 정렬
  const inProgressSets: ProgressItem[] = wordSets
    .filter(set => {
      const progress = learnedCounts[set.id];
      if (!progress) {
        return false;
      }

      const total = progress.knownCount + progress.learningCount;

      return total > 0 && total < set.word_count;
    })
    .map(set => {
      const progress = learnedCounts[set.id]!;

      return {
        id: set.id,
        title: set.title,
        knownCount: progress.knownCount,
        learningCount: progress.learningCount,
        totalCount: set.word_count,
      };
    })
    .sort((a, b) => {
      const aPercent = (a.knownCount + a.learningCount) / a.totalCount;
      const bPercent = (b.knownCount + b.learningCount) / b.totalCount;

      return bPercent - aPercent;
    })
    .slice(0, 3);

  if (!isPending && inProgressSets.length === 0) {
    return null;
  }

  return (
    <div className="rounded-xl bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm font-semibold text-foreground">진행 중인 학습</p>

        <button
          type="button"
          onClick={() => push("/flashcards")}
          className="flex items-center gap-0.5 text-xs text-muted-foreground"
        >
          전체보기
          <ChevronRightIcon className="size-3" />
        </button>
      </div>

      <div className="flex flex-col gap-3.5">
        {isPending ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-28" />
              <Skeleton className="h-3 w-12" />
            </div>
            <Skeleton className="h-1.5 w-full rounded-full" />
          </div>
        ) : (
          inProgressSets.map(set => {
            const done = set.knownCount + set.learningCount;
            const knownPercent = (set.knownCount / set.totalCount) * 100;
            const learningPercent = (set.learningCount / set.totalCount) * 100;

            return (
              <button
                key={set.id}
                type="button"
                onClick={() => push("/flashcards/:id", { id: set.id })}
                className="flex flex-col gap-2 text-left transition-transform active:scale-[0.98]"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm text-foreground truncate">{set.title}</p>
                  <p className="text-xs text-muted-foreground shrink-0 ml-2">
                    {done}/{set.totalCount}
                  </p>
                </div>

                <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <motion.div
                    className="h-full shrink-0 bg-emerald-500"
                    initial={false}
                    animate={{ width: `${knownPercent}%` }}
                    transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                  />
                  <motion.div
                    className="h-full shrink-0 bg-orange-400"
                    initial={false}
                    animate={{ width: `${learningPercent}%` }}
                    transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                  />
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
